import type { LegendListProps, LegendListRef } from "@-/com.npmjs.legendapp__list";
import {
  Platform,
  StyleSheet,
  View,
  type LayoutChangeEvent,
  type StyleProp,
  type ViewStyle,
} from "@-/com.npmjs.react-native";
import { NavigationContext } from "@-/com.npmjs.expo-router/react-navigation";
import { useRouteBodyListScrollAnchor } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import useLatest from "@-/com.npmjs.use-latest";
import { safeRequestAnimationFrame } from "@-/dev.improve.haniverse.lib.scheduler";
import {
  createElement,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type Ref,
} from "react";

/**
 * Persists a list's position inside the nearest `RouteBodyStateProvider`, so a list that is
 * released while hidden reopens where the learner left it. `key` names the list within the route;
 * `scope` names what it shows (a filter, a grade): remounting with a different scope opens at the
 * list's own `initialScrollIndex` instead.
 */
export type HeaderLegendListScrollPosition = { key: string; scope?: string };

type InitialScrollIndex = Exclude<LegendListProps<unknown>["initialScrollIndex"], undefined>;

function topPadding(style: StyleProp<ViewStyle>): number {
  const flattened = StyleSheet.flatten(style) ?? {};
  const padding = flattened.paddingTop ?? flattened.paddingVertical ?? flattened.padding ?? 0;
  return typeof padding === "number" ? padding : 0;
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref != null) {
    ref.current = value;
  }
}

/**
 * Saves the first visible row's key and that row's offset from the viewport top on every scroll,
 * and replays it as the next mount's `initialScrollIndex`. A raw pixel offset would land on the
 * wrong row: a fresh list only has estimated heights for rows it has not measured yet.
 *
 * Also owns the web path for `initialScrollIndex`. LegendList's prop is broken on web: the list
 * allocates a single `estimatedItemSize`-tall slot at `opacity: 0` and never commits any item into
 * it, so the whole list paints blank. Re-verified against `@legendapp/list` 3.3.3 (2026-07-26); re-test
 * on the next upgrade by passing the prop on web and checking the list's scrollHeight exceeds its
 * clientHeight. Web drops the prop and performs the same jump once the list has mounted, which
 * costs one extra frame instead of an empty screen. Native keeps the real prop (no visible jump).
 */
export function useHeaderLegendListScrollPosition<Item>({
  contentContainerStyle,
  data,
  initialScrollIndex,
  keyExtractor,
  ListHeaderComponent,
  onScroll,
  ref,
  scrollPosition,
  style,
}: Pick<
  LegendListProps<Item>,
  | "contentContainerStyle"
  | "data"
  | "initialScrollIndex"
  | "keyExtractor"
  | "ListHeaderComponent"
  | "onScroll"
  | "style"
> & {
  ref?: Ref<LegendListRef>;
  scrollPosition?: HeaderLegendListScrollPosition;
}) {
  const isWeb = Platform.OS === "web";
  const navigation = useContext(NavigationContext);
  const internalRef = useRef<LegendListRef | null>(null);
  const headerHeightRef = useRef(0);
  const { initialAnchor, saveAnchor } = useRouteBodyListScrollAnchor(
    `header-legend-list:${scrollPosition?.key ?? ""}`,
    { contentKey: scrollPosition?.scope },
  );
  const getKey = useCallback(
    (item: Item, index: number) =>
      keyExtractor === undefined ? String(index) : keyExtractor(item, index),
    [keyExtractor],
  );
  // Rows are matched by key: data may have changed while the list was released. A row that is
  // gone falls back to the caller's index.
  const [restoredScrollIndex] = useState((): InitialScrollIndex | undefined => {
    if (scrollPosition === undefined || initialAnchor === null) {
      return undefined;
    }
    const index = (data ?? []).findIndex((item, i) => getKey(item, i) === initialAnchor.key);
    return index === -1
      ? undefined
      : { index, viewOffset: initialAnchor.viewOffset, viewPosition: 0 };
  });
  const effectiveInitialScrollIndex = restoredScrollIndex ?? initialScrollIndex;
  const initialScrollIndexRef = useLatest(effectiveInitialScrollIndex);
  const leadingInsetRef = useLatest(
    topPadding(style) + topPadding(contentContainerStyle as StyleProp<ViewStyle>),
  );
  const onScrollRef = useLatest(onScroll);
  const scrollPositionRef = useLatest(scrollPosition);

  const setRef = useCallback(
    (instance: LegendListRef | null) => {
      internalRef.current = instance;
      assignRef(ref, instance);
    },
    [ref],
  );

  // Only the FIRST mount jumps on web; later data changes must not yank the learner's position.
  const hasJumpedRef = useRef(false);
  useEffect(() => {
    if (!isWeb || hasJumpedRef.current) {
      return;
    }
    const target = initialScrollIndexRef.current;
    const { index, viewOffset, viewPosition } =
      typeof target === "number"
        ? { index: target, viewOffset: 0, viewPosition: 0 }
        : (target ?? {});
    if (index === undefined || (index <= 0 && (viewOffset ?? 0) === 0)) {
      hasJumpedRef.current = true;
      return;
    }
    // Defer past the mount commit so LegendList has containers to scroll to.
    return safeRequestAnimationFrame(() => {
      if (hasJumpedRef.current) {
        return;
      }
      hasJumpedRef.current = true;
      void internalRef.current?.scrollToIndex({ animated: false, index, viewOffset, viewPosition });
    }, "header-legend-list-initial-scroll");
  }, [initialScrollIndexRef, isWeb]);

  const handleScroll = useCallback<NonNullable<LegendListProps<Item>["onScroll"]>>(
    (event) => {
      onScrollRef.current?.(event);
      if (scrollPositionRef.current === undefined) {
        return;
      }
      // Native screen detachment can emit a clamped offset while the route blurs, and a throttled
      // trailing call can land after it; only a visible list owns the saved position.
      if (navigation?.isFocused() === false) {
        return;
      }
      const state = internalRef.current?.getState();
      if (state === undefined) {
        return;
      }
      const item: Item | undefined = state.data[state.start];
      const position = state.positionAtIndex(state.start);
      if (item === undefined || !Number.isFinite(position)) {
        return;
      }
      saveAnchor({
        key: getKey(item, state.start),
        // Negative once the row's top has scrolled above the viewport. LegendList subtracts
        // `viewOffset` from the row's offset, so replaying it puts the row back at this spot.
        viewOffset: position + leadingInsetRef.current + headerHeightRef.current - state.scroll,
      });
    },
    [getKey, leadingInsetRef, navigation, onScrollRef, saveAnchor, scrollPositionRef],
  );

  // LegendList positions rows below its header, so the saved offset needs the header's height.
  const onHeaderLayout = useCallback((event: LayoutChangeEvent) => {
    headerHeightRef.current = event.nativeEvent.layout.height;
  }, []);
  const header =
    scrollPosition === undefined || ListHeaderComponent == null ? (
      ListHeaderComponent
    ) : (
      <View onLayout={onHeaderLayout}>
        {isValidElement(ListHeaderComponent)
          ? ListHeaderComponent
          : createElement(ListHeaderComponent)}
      </View>
    );

  return {
    initialScrollIndex: isWeb ? undefined : effectiveInitialScrollIndex,
    ListHeaderComponent: header,
    onScroll: scrollPosition === undefined ? onScroll : handleScroll,
    ref: setRef,
  };
}
